import { useInfiniteQuery } from '@tanstack/react-query';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useInView } from "react-intersection-observer";
import { ReactComponent as GridIcon } from '../assets/icons/grid.svg';
import { ReactComponent as ListIcon } from '../assets/icons/list.svg';
import BomDesigner from '../components/designer/BomDesigner';
import AssetFilter from '../components/layout/AssetFilter';
import AssetList from '../components/layout/AssetList';
import Layout from '../components/layout/Layout';
import Overlay from '../components/layout/Overlay';
import { BomDesignerContext } from '../contexts/BomDesignerContext';
import { ConfigContext } from '../contexts/ConfigContext';
import { GenerateODataFilter } from '../helpers/odataFunctions';
import { useAssetServiceGetAssetMetadataValues, useBomModelServiceGetBomModelKey } from '../openapi/queries';
import { BomModelService, BomModelViewDTO } from '../openapi/requests';
import { Filter } from '../types/types';
import { useNavigate, useSearchParams } from 'react-router-dom';

const BomModelsPage: React.FC = () => {
    const [ searchParams, setSearchParams ] = useSearchParams();

    const navigate = useNavigate();
    const {createNew, load} = useContext(BomDesignerContext);
    const { odataAssetFilters } = useContext(ConfigContext);
    const [mode, setMode] = useState<"medium" | "list">("medium");
    const [filters, setFilters] = useState<Filter[]>([]);
    const [formOpen, setFormOpen] = useState(false);
    const { ref, inView } = useInView();


    const { data: brandMetadata } = useAssetServiceGetAssetMetadataValues({ name: "brand", type: 'bommodel' });
    const { data: rangeMetadata } = useAssetServiceGetAssetMetadataValues({ name: "product_range", type: 'bommodel' });
    const { data: productTypeMetadata } = useAssetServiceGetAssetMetadataValues({ name: "product_type_primary", type: 'bommodel' });
    const { data: categoryMetadata } = useAssetServiceGetAssetMetadataValues({ name: "category", type: 'bommodel' });

    const brands = useMemo(() => {
        return brandMetadata?.map(e => ({value: e})) ?? [];
    }, [brandMetadata]);

    const ranges = useMemo(() => {
        return rangeMetadata?.map(e => ({value: e})) ?? [];
    }, [rangeMetadata]);

    const productTypes = useMemo(() => {
        return productTypeMetadata?.map(e => ({value: e})) ?? [];
    }, [productTypeMetadata]);

    const categories = useMemo(() => {
        return categoryMetadata?.map(e => ({value: e})) ?? [];
    }, [categoryMetadata]);

    const {
        data: models,
        fetchNextPage,
        isFetching,
        refetch
    } = useInfiniteQuery({
        queryKey: [useBomModelServiceGetBomModelKey, filters, odataAssetFilters],
        initialPageParam: "0",

        queryFn: async ({ pageParam = "0" }) => {
            let combinedFilters = [...filters, ...odataAssetFilters];
            return await BomModelService.getBomModel(undefined, undefined, pageParam, GenerateODataFilter(combinedFilters), undefined, undefined, undefined, 'true')
        },
        getNextPageParam: (lastPage, allPages) => {
            if (lastPage["@odata.nextLink"]) {
                let url = new URL(lastPage["@odata.nextLink"]);
                return url.searchParams.get("$skip")
            }
            return null;
        }
    });

    useEffect(() => {
        if(searchParams.get("modelId")){
            const id = parseInt(searchParams.get("modelId")!);
            load(id);
            setFormOpen(true);
        }else{
            setFormOpen(false);
        }
    }, [load, searchParams]);

    useEffect(() => {
        if (inView) {
            !isFetching && fetchNextPage()
        }
    }, [inView, fetchNextPage, isFetching])

    const filteredModels = useMemo(() => {
        return models?.pages.map(x => x.value).flat() ?? []
    }, [models]);

    const handleCreate = useCallback(() => {
        createNew();
        setFormOpen(true);
    }, [createNew]);

    const handleEdit = useCallback((asset: BomModelViewDTO) => {
        setSearchParams({modelId: asset.id.toString()});
    }, [setSearchParams]);

    const handleClose = useCallback(() => {
        setSearchParams(undefined);
        setFormOpen(false);
    }, [setSearchParams]);

    return (<Layout className='categories'>
        <header>
            <div>
                <button onClick={handleCreate}>Create new</button>
            </div>
            <div>
                <button className={(mode === "medium") ? 'selected' : 'deselected'} onClick={() => setMode("medium")}>
                    <GridIcon />
                </button>
                <button className={(mode === "list") ? 'selected' : 'deselected'} onClick={() => setMode("list")}>
                    <ListIcon />
                </button>
            </div>
            <div className='header-filter'>
            </div>
        </header>

        <div className='categories-content'>
            <AssetFilter filters={filters} onChange={e => setFilters(e)} items={[]} properties={[
                                { name: 'Search', property: 'name,title,metadata.ean', type: 'search' },
                                { name: 'Brand', property: 'metadata.brand', type: 'select', itemsOverride: brands },
                                { name: 'Range', property: 'metadata.product_range', type: 'select', itemsOverride: ranges },
                                { name: 'Type', property: 'metadata.product_type_primary', type: 'select', itemsOverride: productTypes },
                                { name: 'Category', property: 'metadata.category', type: 'select', itemsOverride: categories },
                            ]} />

            <AssetList
                loading={isFetching}
                assets={filteredModels}
                onItemClick={handleEdit}
                displayMode={mode} />

            <div ref={ref} />
        </div>

        <Overlay open={formOpen} onClose={handleClose}>
            <BomDesigner onSave={refetch} />
        </Overlay>

    </Layout>)
};

export default BomModelsPage;